import type { Coding } from '@medplum/fhirtypes'
import { formatClinicalUnit } from '@seen/core/display'
import { getCodingDetails } from '@seen/core/models/codingDetails'
import { ExtendedObservation } from '@seen/core/models/observation'
import { unitLabel } from '@seen/core/models/observationComponents'
import type { ObservationValue } from '@seen/core/observation-logic/value'
import {
  type ResourceBindingId,
  resourceBinding,
} from '@seen/core/resourceBindingRegistry'
import { Button, cn } from '@seen/ui'
import { IconMathFunction, IconRefresh } from '@tabler/icons-react'
import { format, parseISO } from 'date-fns'
import { orderBy } from 'lodash-es'
import { type JSX, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#src/components/core/popover/index.ts'
import {
  DerivationExplanation,
  DerivationSourceList,
} from '#src/components/rich-text-editor/ObservationNode/derived/DerivationExplanation.tsx'
import { useDerivedObservation } from '#src/components/rich-text-editor/ObservationNode/derived/useDerivedObservation.ts'
import { observationNodeContent } from '#src/components/rich-text-editor/ObservationNode/observationNodeContent.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  CHOOSE_PARTICIPANT,
  captureRecordFor,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useRead } from '#src/hooks/useRead.ts'

// An age resolves to nothing when the birth date is missing, which is what
// the chart has to be given.
const REQUIREMENT_LABELS: Partial<Record<ResourceBindingId, string>> = {
  'participant.age-years': 'participant date of birth',
}

function requirementName(id: ResourceBindingId): string {
  return (
    REQUIREMENT_LABELS[id] ?? resourceBinding(id)?.label.toLowerCase() ?? id
  )
}

function listed(items: string[]): string {
  return items.length > 1
    ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`
    : (items[0] ?? '')
}

function inputName(coding: Coding): string {
  return coding.display ?? coding.code ?? ''
}

function numberOf(value: ObservationValue | null): number | undefined {
  return value?.kind === 'number' ? value.value : undefined
}

// A computed observation (BMI, MAP, BSA, eGFR, ideal body weight, estimated
// needs): its value from the inputs recorded before it in the note, or the
// participant's latest records, with a function glyph that opens how it is
// calculated (formula, inputs, cutoffs and their sources) and is labelled
// with what it is calculated from and still waits for. The value can be
// changed by hand, which stops recalculation until asked.
export function ObservationNodeDerived({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { participant, noParticipant } = capture
  const isReadOnly = useIsReadOnly()
  const { data: record } = useRead(ExtendedObservation, attributes.reference)
  // Another participant's record is shown, never recalculated from this
  // participant's inputs.
  const foreign = isForeignRecord(record, captureRecordFor(capture))
  const editable = capture.editable && !isReadOnly && !noParticipant && !foreign
  const medplum = useMedplum()

  const derived = useDerivedObservation({
    editor: props.editor,
    getPos: props.getPos,
    attributes,
    updateAttributes: props.updateAttributes,
    participant,
    recordStatus: capture.recordStatus,
    deferred: capture.deferred,
    editable,
  })
  const shownNumber = numberOf(derived.shown)
  const [draft, setDraft] = useState(
    shownNumber === undefined ? '' : String(shownNumber)
  )
  useEffect(() => {
    setDraft(shownNumber === undefined ? '' : String(shownNumber))
  }, [shownNumber])

  const commit = (): void => {
    const next = draft.trim() === '' ? undefined : Number(draft)
    if (next !== undefined && Number.isNaN(next)) {
      return
    }
    if (next !== shownNumber) {
      derived.setManualValue(next)
    }
  }

  const addMissing = async (): Promise<void> => {
    const pos = props.getPos()
    if (pos === undefined) {
      return
    }
    try {
      const nodes = await Promise.all(
        derived.absentFromNote.map(async (coding) =>
          observationNodeContent(
            coding,
            await getCodingDetails(medplum, coding)
          )
        )
      )
      props.editor
        .chain()
        .insertContentAt(
          pos + props.node.nodeSize,
          nodes.flatMap((node) => [{ type: 'text', text: ' ' }, node])
        )
        .run()
    } catch (error) {
      toast.error(
        `Failed to add ${listed(derived.absentFromNote.map(inputName))}${error instanceof Error ? `: ${error.message}` : ''}`
      )
    }
  }

  const unit =
    derived.shown?.kind === 'number' && derived.shown.unit
      ? formatClinicalUnit(unitLabel(derived.shown.unit))
      : undefined
  const needs = [
    ...derived.missingInputs.map(inputName),
    ...derived.missingRequirements.map(requirementName),
  ]
  // An optional input that went unused is not named.
  const fromNames = derived.sources.flatMap((source) => {
    const name = inputName(source.coding)
    if (source.value === null) {
      return source.optional ? [] : [name]
    }
    return [
      source.origin === 'record'
        ? `${name} (latest record)`
        : source.origin === 'history'
          ? `${name} (${source.date ?? 'record'})`
          : source.origin === 'default'
            ? `${name} (default)`
            : name,
    ]
  })
  // Where the inputs were captured, named on the line so a value is never
  // computed from records nobody can see.
  const used = derived.sources.filter((source) => source.value !== null)
  const fromChart = used.filter(
    (source) => source.origin === 'record' || source.origin === 'history'
  )
  const fromNote = used.some((source) => source.origin === 'note')
  const [oldestChartDay] = orderBy(
    fromChart.flatMap((source) => (source.date ? [source.date] : [])),
    [(day) => day],
    ['asc']
  )
  const waiting = needs.length > 0 && !derived.override
  const explanation = [
    `Calculated from ${listed(fromNames)}`,
    ...(waiting ? [`waiting for ${listed(needs)}`] : []),
  ].join(' · ')

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={editable ? props.updateAttributes : undefined}
      participant={derived.recorded?.subject ?? participant}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      valueInDetails={false}
      foreign={foreign}
    >
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        <input
          aria-label={attributes.codingDisplay}
          type="number"
          value={draft}
          disabled={!editable}
          placeholder="—"
          className="w-[7ch] rounded-[calc(var(--radius)-5px)] bg-transparent font-mono tabular-nums placeholder:text-placeholder focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commit()
            }
          }}
        />
        {unit ? (
          <span className="text-foreground-secondary">{unit}</span>
        ) : null}
        {derived.band ? (
          <span className="text-foreground-secondary">
            · {derived.band.display}
          </span>
        ) : null}
        {used.length > 0 && !derived.override && shownNumber !== undefined ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-derived-from-chart={fromChart.length > 0 ? '' : undefined}
                data-derived-source=""
                className="self-center rounded-sm text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-none"
              >
                {fromChart.length > 0
                  ? `${fromNote ? 'from note and chart' : 'from chart'}${
                      oldestChartDay
                        ? ` · ${format(parseISO(oldestChartDay), 'MMM d, yyyy')}`
                        : ''
                    }`
                  : 'from this note'}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="space-y-1 w-80">
              <p className="text-xs font-medium text-foreground-secondary">
                Calculated from
              </p>
              <DerivationSourceList
                sources={used}
                subject={derived.recorded?.subject ?? participant}
              />
            </PopoverContent>
          </Popover>
        ) : null}
        {derived.override ? (
          <button
            type="button"
            aria-label="Recalculate"
            title="Set by hand · Recalculate"
            data-derived-override=""
            disabled={!editable}
            className="inline-flex self-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-none disabled:pointer-events-none"
            onClick={() => props.updateAttributes({ derivedOverride: false })}
          >
            <IconRefresh className="size-3.5" aria-hidden />
          </button>
        ) : null}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={explanation}
              data-derived-indicator={waiting ? 'waiting' : 'calculated'}
              className={cn(
                'inline-flex self-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950',
                waiting ? 'text-amber-600' : 'text-muted-foreground'
              )}
            >
              <IconMathFunction className="size-3.5" aria-hidden />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-96">
            <DerivationExplanation
              target={attributes.codingDisplay}
              rule={derived.rule}
              sources={derived.sources}
              requirementValues={derived.requirementValues}
              shown={derived.shown}
              override={derived.override}
              band={derived.band}
              bandSet={derived.bandSet}
              subject={derived.recorded?.subject ?? participant}
            />
          </PopoverContent>
        </Popover>
        {waiting && editable && derived.absentFromNote.length > 0 ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            aria-label={`Add ${listed(derived.absentFromNote.map(inputName))}`}
            className="h-auto p-0 text-xs"
            onClick={() => void addMissing()}
          >
            Add
          </Button>
        ) : null}
      </span>
    </ObservationNodeFrame>
  )
}
